if (!customElements.get('marame-back-in-stock')) {
  customElements.define('marame-back-in-stock', class MarameBackInStock extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-notify-form]');
      this.email = this.querySelector('[data-notify-email]');
      this.submitButton = this.querySelector('[data-notify-submit]');
      this.status = this.querySelector('[data-notify-status]');
      this.error = this.querySelector('[data-notify-error]');
      this.item = this.querySelector('[data-notify-item]');
      this.requests ||= new Map();
      this.accepted ||= new Set();
      this.uncertain ||= new Set();
      this.viewToken ||= 0;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-marame-notify]');
        if (!trigger || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
        const details = { variantId: trigger.dataset.variantId, productTitle: trigger.dataset.productTitle, variantTitle: trigger.dataset.variantTitle, trigger };
        if (this.show(details)) event.preventDefault();
      }, options);
      document.addEventListener('marame:notify', (event) => this.show(event.detail || {}), options);
      this.form.addEventListener('submit', (event) => this.submit(event), options);
      this.querySelector('[data-notify-close]').addEventListener('click', () => this.hide(), options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.hide(); }, options);
      this.dialog.addEventListener('pointerdown', (event) => { this.backdropDown = this.isBackdrop(event); }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropDown && this.isBackdrop(event)) this.hide();
        this.backdropDown = false;
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.hasAttribute('open')) this.hide();
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.viewToken++;
      if (this.ownsBodyLock) document.body.classList.remove('overflow-hidden');
    }

    validVariant(id) {
      return /^[1-9]\d{0,19}$/.test(String(id || ''));
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    show(details) {
      if (!this.validVariant(details.variantId) || !this.dialog?.showModal) return false;
      this.viewToken++;
      this.variantId = String(details.variantId);
      this.openedBy = details.trigger || document.activeElement;
      this.parentBag = this.openedBy?.closest?.('cart-drawer');
      if (this.parentBag?.classList.contains('active') && typeof removeTrapFocus === 'function') removeTrapFocus();
      this.item.textContent = [details.productTitle, details.variantTitle].filter(Boolean).join(' — ');
      this.form.reset();
      this.form.hidden = false;
      this.error.hidden = true;
      this.status.hidden = true;
      this.setBusy(false);
      if (!this.dialog.open) {
        this.ownsBodyLock = !document.body.classList.contains('overflow-hidden');
        document.body.classList.add('overflow-hidden');
        this.dialog.showModal();
      }
      this.setAttribute('open', '');
      this.email.focus({ preventScroll: true });
      return true;
    }

    hide() {
      this.viewToken++;
      this.dialog.close();
      this.removeAttribute('open');
      this.form.reset();
      if (this.ownsBodyLock) document.body.classList.remove('overflow-hidden');
      this.ownsBodyLock = false;
      if (this.parentBag?.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(this.parentBag.querySelector('.drawer__inner'), this.openedBy?.isConnected ? this.openedBy : this.parentBag.querySelector('.drawer__close'));
      } else if (this.openedBy?.isConnected) {
        this.openedBy.focus({ preventScroll: true });
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.submitButton.disabled = busy;
      this.email.disabled = busy;
      this.submitButton.textContent = busy ? this.dataset.pendingLabel : this.dataset.submitLabel;
    }

    requestSubscription(variantId, email) {
      if (!this.validVariant(variantId) || !/^[a-zA-Z0-9]{6}$/.test(this.dataset.publicKey || '')) return Promise.resolve({ code: 'unavailable' });
      const key = `${variantId}:${email.toLowerCase()}`;
      if (this.accepted.has(key)) return Promise.resolve({ code: 'accepted' });
      if (this.uncertain.has(key)) return Promise.resolve({ code: 'uncertain' });
      if (this.requests.has(key)) return this.requests.get(key);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      const payload = { data: {
        type: 'back-in-stock-subscription',
        attributes: { profile: { data: { type: 'profile', attributes: { email } } }, channels: ['EMAIL'] },
        relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } }
      } };
      const request = (async () => {
        try {
          const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.dataset.publicKey)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
            body: JSON.stringify(payload),
            credentials: 'omit',
            redirect: 'error',
            signal: controller.signal
          });
          if (response.status === 202) {
            this.accepted.add(key);
            return { code: 'accepted' };
          }
          if (response.status === 404) return { code: 'unavailable' };
          if (response.status === 429) return { code: 'rate' };
          return { code: 'error' };
        } catch {
          // A connection failure may occur after acceptance. Never auto-resubmit it.
          this.uncertain.add(key);
          return { code: 'uncertain' };
        } finally {
          clearTimeout(timeout);
          this.requests.delete(key);
        }
      })();
      this.requests.set(key, request);
      return request;
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.email.value = this.email.value.trim();
      if (!this.form.reportValidity()) return;
      const token = this.viewToken;
      const variantId = this.variantId;
      const email = this.email.value;
      this.error.hidden = true;
      this.status.hidden = true;
      this.setBusy(true);
      const result = await this.requestSubscription(variantId, email);
      if (token !== this.viewToken || !this.dialog.open || !this.isConnected) return;
      this.setBusy(false);
      if (result.code === 'accepted') {
        this.form.hidden = true;
        this.form.reset();
        this.status.textContent = this.dataset.successLabel;
        this.status.hidden = false;
        this.status.focus({ preventScroll: true });
      } else {
        this.error.textContent = this.dataset[`${result.code}Label`] || this.dataset.errorLabel;
        this.error.hidden = false;
        if (result.code === 'uncertain') this.form.hidden = true;
      }
    }
  });
}
